import {
    GoogleMap,
    GoogleMapProps,
    InfoWindow,
    Marker,
    useJsApiLoader,
} from '@react-google-maps/api'
import { useCallback, useRef, useState } from 'react'
import markerIcon from '../../assets/img/bxs-map.png'
import { getDistance } from 'geolib'

interface MarkerData<T> {
    latitude: number
    longitude: number
    popupData?: T
    icon?: string
}

interface MapBoxProps<T> {
    markers: MarkerData<T>[]
    renderPopupContent?: (data: T) => JSX.Element
    onBoundsChanged?: (bounds: {
        latitude: number
        longitude: number
        radius: number
    }) => void
}

const containerStyle = {
    width: '100%',
    height: '600px',
}

const defaultCenter = { lat: 51.51830379, lng: -0.15591514 }
const defaultZoom = 15.5

export default function GMap<T>({
    markers,
    renderPopupContent,
    onBoundsChanged,
    ...props
}: MapBoxProps<T>) {
    const [selectedMarkerIndex, setSelectedMarkerIndex] = useState<
        number | null
    >(null)
    const mapRef = useRef<google.maps.Map | null>(null)

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAP_ACCESS_TOKEN,
    })

    const onLoad = useCallback(
        (map: google.maps.Map) => {
            mapRef.current = map

            if (markers.length) {
                const bounds = new window.google.maps.LatLngBounds()
                markers.forEach(({ latitude, longitude }) => {
                    bounds.extend({ lat: latitude, lng: longitude })
                })
                map.fitBounds(bounds)
            }
        },
        [markers],
    )

    const onUnmount = useCallback(() => {
        mapRef.current = null
    }, [])

    return isLoaded ? (
        <GoogleMap
            mapContainerStyle={containerStyle}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onBoundsChanged={() => {
                const map = mapRef.current
                if (!map) return

                const center = map.getCenter()
                const bounds = map.getBounds()

                if (!center || !bounds) return

                const northEast = bounds.getNorthEast()

                const radius = getDistance(
                    { latitude: center.lat(), longitude: center.lng() },
                    { latitude: northEast.lat(), longitude: northEast.lng() },
                )

                onBoundsChanged?.({
                    latitude: center.lat(),
                    longitude: center.lng(),
                    radius: radius / 1000, // km,
                })
            }}
            center={markers.length === 0 ? defaultCenter : undefined}
            zoom={markers.length === 0 ? defaultZoom : undefined}
            options={{
                mapTypeId: 'roadmap',
                streetViewControl: false,
                fullscreenControl: false,
                mapTypeControl: false,
                zoomControl: true,
            }}
            {...props}
        >
            {markers.map((marker, i) => (
                <Marker
                    key={i}
                    position={{ lat: marker.latitude, lng: marker.longitude }}
                    icon={marker.icon || markerIcon}
                    onClick={() => setSelectedMarkerIndex(i)}
                />
            ))}

            {selectedMarkerIndex !== null &&
                markers[selectedMarkerIndex].popupData &&
                renderPopupContent && (
                    <InfoWindow
                        position={{
                            lat: markers[selectedMarkerIndex].latitude,
                            lng: markers[selectedMarkerIndex].longitude,
                        }}
                        onCloseClick={() => setSelectedMarkerIndex(null)}
                    >
                        <div>
                            {renderPopupContent(
                                markers[selectedMarkerIndex].popupData!,
                            )}
                        </div>
                    </InfoWindow>
                )}
        </GoogleMap>
    ) : null
}
